// R93 — "NOTHING INSIDE THE PAGES" IS WHAT THIS HOUSE SAID WHEN THE SEARCH NEVER RAN.
//
// **THE COMPONENT STATES THE LAW AND THE HOOK TWO FILES AWAY BROKE IT** — R23's shape, and R92's,
// one room over. `src/components/BookHitList.tsx` opens with *"SIX STATES, AND EACH ONE OWES THE
// MEMBER A DIFFERENT SENTENCE. A search box that renders 'nothing found' while it is still
// searching, or renders nothing at all when she is signed out, teaches her the library has no text
// in it."* `useBookSearch` could only ever produce FIVE of them, because a search that FAILED and a
// search that genuinely matched nothing were byte-identical:
//
//     { loading: false, hits: [], mode: "", ran: true, needsAuth: false }
//
// THREE PATHS REACHED THAT STATE AND NONE OF THEM WAS AN EMPTY RESULT:
//
//   * `.catch(() => …)` — the request never landed at all (a phone leaving a lift, a restart
//     mid-deploy). R18's TRANSPORT NOISE, drawn as a fact about her library.
//   * **HTTP 500, where the server composed a real sentence — "Search is unavailable right now." —
//     that reached nobody.** `data.results` is undefined on that body, so `Array.isArray(…) ? … :
//     []` fabricated the empty list and `ran: true` published it. R51/R60's finding again: the house
//     was already honest and the client threw the words away.
//   * `mode: "unavailable"`, which is the server's OWN deliberate name for "there is no stored text
//     to search" (its comment: *"No database configured is an honest empty state, never a 500"*).
//     **It was carried across the wire into `state.mode` and READ BY NOTHING** — R38's capability
//     wired to nothing, and the one state where "nothing found" is not merely unhelpful but the
//     exact opposite of true.
//
// **THE SENTENCE IT DREW IS THE WORST AVAILABLE ONE, AND THAT IS R60's CARD-LIBRARY FINDING WORD FOR
// WORD: THE FAILURE STATE WAS NOT A MISSING SENTENCE BUT A WRONG ONE.** *"Nothing inside the pages
// for 'saturn return'"* is a claim about 28.8M characters of ingested book text, and a member who
// reads it twice stops using the search — she has been told, in her own language, that the thing she
// came for is not in there.
//
// So the transport lives here ONCE (R31: the crossing stops existing rather than being watched) and
// hands back an ANSWER rather than a status code. `doFetch` is a PARAMETER (R46's locked-door shape)
// so a gate drives every arm the route can give without a browser, a session or a database.
//
// This module imports NOTHING but `import type` — the entry requirement that lets a `.mjs` gate load
// and RUN it.
import type { BookHit } from "../components/BookHitList";

export const librarySearchPath = "/api/library/search";

// The route is behind sign-in and composes a real sentence on 500, so the FOUR-WAY answer is right
// here and a narrower one would be a second answer key (R58's ruling, argued per route rather than
// made uniform). `unavailable` is NOT one of these: it is a successful search of a library that has
// no stored text, which is a fact about the shelf and not about the request.
export type BookSearchAnswer =
  | { ok: true; hits: BookHit[]; mode: string }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

export function librarySearchUrl(q: string, slug = ""): string {
  return `${librarySearchPath}?q=${encodeURIComponent(q)}${slug ? `&slug=${encodeURIComponent(slug)}` : ""}`;
}

export async function searchBooks(
  q: string,
  slug = "",
  doFetch: typeof fetch = fetch,
): Promise<BookSearchAnswer> {
  try {
    const res = await doFetch(librarySearchUrl(q, slug));
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = (await res.json().catch(() => null)) as { results?: unknown; mode?: unknown; message?: unknown } | null;
    if (!res.ok) {
      const message = typeof data?.message === "string" ? data.message : "";
      return { ok: false, kind: "refused", message };
    }
    // **A 200 CARRYING NO `results` IS A REFUSAL, NOT AN EMPTY SHELF** (R53's `data` guard, R18's
    // distinction): the route sends an array on every path it reaches, so its absence means the body
    // was not the one this house writes.
    if (!Array.isArray(data?.results)) return { ok: false, kind: "refused", message: "" };
    return { ok: true, hits: data.results as BookHit[], mode: typeof data.mode === "string" ? data.mode : "" };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// **THE STATE CARRIES THE ANSWER; THE SENTENCE IS DERIVED AT RENDER** (R58/R60's shape), so switching
// language repaints the line she is looking at rather than leaving it in the language she just left.
export type BookSearchFailure = { kind: "refused"; message: string } | { kind: "offline" } | { kind: "unavailable" };

export type BookSearchState = {
  loading: boolean;
  hits: BookHit[];
  mode: string;
  ran: boolean;
  needsAuth: boolean;
  // R93 — THE SIXTH STATE THIS COMPONENT'S OWN HEADER PROMISED. `null` means the search really ran
  // and really matched nothing, which is the only state allowed to say "nothing inside the pages".
  failed: BookSearchFailure | null;
};

export const IDLE_SEARCH: BookSearchState = { loading: false, hits: [], mode: "", ran: false, needsAuth: false, failed: null };

// One place turns an answer into the next state, so the two call sites (the library-wide box and the
// in-book box) cannot drift — they were the same nine lines typed twice.
export function searchStateFrom(answer: BookSearchAnswer): BookSearchState {
  if (answer.ok) {
    return {
      loading: false,
      hits: answer.hits,
      mode: answer.mode,
      ran: true,
      needsAuth: false,
      // The server's own honest name for a shelf with no stored text, finally read.
      failed: answer.mode === "unavailable" ? { kind: "unavailable" } : null,
    };
  }
  if (answer.kind === "sign-in") return { ...IDLE_SEARCH, ran: true, needsAuth: true };
  return { ...IDLE_SEARCH, ran: true, failed: answer };
}

// The server's sentence wins where it sent one (R51: it already says why, and it says it better than
// a generic line can); otherwise the house's own. `unavailable` is a DIFFERENT fact from a failed
// request and keeps its own row — one is "we could not ask", the other is "there is nothing here to
// ask of", and telling her to try again would be advice that cannot work.
export function searchFailureNote(
  failed: BookSearchFailure | null,
  copy: { failed: string; unavailable: string },
): string {
  if (!failed) return "";
  if (failed.kind === "unavailable") return copy.unavailable;
  if (failed.kind === "refused" && failed.message) return failed.message;
  return copy.failed;
}
